<template>
  <div>
    <v-row dense>
      <v-col cols="12" md="3" lg="2">
        <v-autocomplete
          v-model="model_managers"
          :items="service_managers"
          item-value="id"
          item-text="user_name"
          outlined
          dense
          hide-details
          multiple
          :placeholder="$capitalize($tc('model.lead_filter_managers'))"
          :return-object="true"
          class="my-0"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0" class="grey--text">{{ $capitalize($tc('model.lead_filter_managers')) }}</span>
          </template>
          <template v-slot:append>
            <v-avatar
              size="20"
              color="indigo"
              v-if="model_managers.length > 0"
            >
              <span class="white--text text-caption">{{
                model_managers.length
              }}</span>
            </v-avatar>
            <v-icon right>mdi-menu-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-avatar>
              <user-avatar
                :user_name="item.user_name"
                :avatar_url="item.user_avatar"
                size="30"
                :user_id="item.user_id"
              />
            </v-list-item-avatar>
            <v-list-item-title>{{ item.user_name }}</v-list-item-title>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" lg="2">
        <v-autocomplete
          v-model="model_statuses"
          :items="lead_statuses"
          outlined
          dense
          multiple
          hide-details
          :placeholder="$capitalize($tc('model.lead_filter_status'))"
          item-value="id"
          item-text="name"
          :return-object="true"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0" class="grey--text mr-2">{{ $capitalize($tc('model.lead_filter_status')) }}</span>
          </template>
          <template v-slot:append>
            <v-avatar
              size="20"
              color="red"
              v-if="model_statuses.length > 0"
            >
              <span class="white--text text-caption">{{
                model_statuses.length
              }}</span>
            </v-avatar>
            <v-icon right>mdi-menu-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>
                <v-icon small :color="item.color" left
                  >mdi-checkbox-blank-circle</v-icon
                >
                {{ item.name }}
              </v-list-item-title>
              <v-list-item-subtitle>{{
                item.description
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" lg="2">
        <v-autocomplete
          v-model="model_types"
          :items="lead_types"
          outlined
          dense
          multiple
          hide-details
          :placeholder="$capitalize($tc('model.lead_filter_types'))"
          item-value="id"
          item-text="description"
          :return-object="true"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0" class="grey--text mr-2">{{ $capitalize($tc('model.lead_filter_types')) }}</span>
          </template>
          <template v-slot:append>
            <v-avatar
              size="20"
              color="orange"
              v-if="model_types.length > 0"
            >
              <span class="white--text text-caption">{{
                model_types.length
              }}</span>
            </v-avatar>
            <v-icon right>mdi-menu-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            {{ item.description }}
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" lg="2">
        <v-autocomplete
          v-model="model_insurers"
          :items="insurers"
          :search-input.sync="search_insurer"
          outlined
          dense
          multiple
          hide-details
          :placeholder="$capitalize($tc('model.lead_filter_company'))"
          item-value="id"
          item-text="name"
          :return-object="true"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0" class="grey--text mr-2">{{ $capitalize($tc('model.lead_filter_company')) }}</span>
          </template>
          <template v-slot:append>
            <v-avatar
              size="20"
              color="cyan"
              v-if="model_insurers.length > 0"
            >
              <span class="white--text text-caption">{{
                model_insurers.length
              }}</span>
            </v-avatar>
            <v-icon right>mdi-menu-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            {{ item.name }}
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" lg="2">
        <v-autocomplete
          v-model="model_insureds"
          :items="insureds"
          :search-input.sync="search_insured"
          outlined
          dense
          multiple
          hide-details
          :placeholder="$capitalize($tc('model.lead_filter_insured'))"
          item-value="id"
          item-text="name"
          :return-object="true"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0" class="grey--text mr-2">{{ $capitalize($tc('model.lead_filter_insured')) }}</span>
          </template>
          <template v-slot:append>
            <v-avatar
              size="20"
              color="green"
              v-if="model_insureds.length > 0"
            >
              <span class="white--text text-caption">{{
                model_insureds.length
              }}</span>
            </v-avatar>
            <v-icon right>mdi-menu-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            {{ item.name }}
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
    <v-row dense class="mt-2">
      <v-chip
        v-for="manager in model_managers"
        color="indigo"
        :key="'manager-' + manager.id"
        class="white--text mr-1 my-1"
        small
        close
        @click:close="remove('model_managers', manager)"
      >
        {{ manager.user_name }}
      </v-chip>
      <v-chip
        v-for="status in model_statuses"
        color="red"
        :key="'status-' + status.id"
        class="white--text mr-1 my-1"
        small
        close
        @click:close="remove('model_statuses', status)"
      >
        {{ status.name }}
      </v-chip>
      <v-chip
        v-for="type in model_types"
        color="orange"
        :key="'type-' + type.id"
        class="white--text mr-1 my-1"
        small
        close
        @click:close="remove('model_types', type)"
      >
        {{ type.description }}
      </v-chip>
      <v-chip
        v-for="insured in model_insureds"
        color="green"
        :key="'insured-' + insured.id"
        class="white--text mr-1 my-1"
        small
        close
        @click:close="remove('model_insureds', insured)"
      >
        <span
          class="d-inline-block text-truncate"
          style="max-width: 190px"
        >
          {{ insured.name }}
        </span>
      </v-chip>
      <v-chip
        v-for="insurer in model_insurers"
        color="cyan"
        :key="'insurer-' + insurer.id"
        class="white--text mr-1 my-1"
        small
        close
        @click:close="remove('model_insurers', insurer)"
      >
        <span
          class="d-inline-block text-truncate"
          style="max-width: 190px"
        >
          {{ insurer.name }}
        </span>
      </v-chip>
    </v-row>
    <v-row class="mt-2">
      <v-btn small text @click="cleanFilter">{{ $tc('model.lead_filter_clean_filter') }}</v-btn>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import UserAvatar from "@/components/UserAvatar";

export default {
  props: ['value'],
  components: { UserAvatar },
  data: () => ({
    fetching_insured: false,
    search_insured: "",
    insureds: [],
    fetching_insurer: false,
    search_insurer: "",
    insurers: []
  }),
  methods: {
    remove(model, item) {
      this[model] = this[model].filter(record => record.id != item.id);
    },
    cleanFilter() {
      this.$emit('input', {
        managers: [],
        statuses: [],
        types: [],
        insureds: [],
        insurers: []
      });
      this.$listeners.fetch();
    },
    async fetchInsured(filter) {
      if (this.fetching_insured) return;

      this.fetching_insured = true;
      const response = await this.$http.get("/contacts", {
        params: { filter }
      });
      this.insureds = response.data.data;
      this.insureds.push(this.model_insureds);
      this.fetching_insured = false;
    },
    async fetchInsurer(filter) {
      if (this.fetching_insurer) return;

      this.fetching_insurer = true;
      const response = await this.$http.get("/contacts", {
        params: { filter }
      });
      this.insurers = response.data.data;
      this.insurers.push(this.model_insurers);
      this.fetching_insurer = false;
    },
    ...mapActions("lead_statuses", ["loadStatuses"]),
    ...mapActions("lead_types", ["loadTypes"]),
    ...mapActions("service_managers", ["loadServiceManagers"])
  },
  computed: {
    model_managers: {
      get: function() {
        return this.value.managers ? this.value.managers : [];
      },
      set: function(newValue) {
        if (newValue) {
          this.$emit('input', {
            ...this.value,
            managers: newValue ? newValue : []
          });

          this.$listeners.fetch();
        }
      }
    },
    model_statuses: {
      get: function() {
        return this.value.statuses ? this.value.statuses : [];
      },
      set: function(newValue) {
        if (newValue) {
          this.$emit('input', {
            ...this.value,
            statuses: newValue ? newValue : []
          });

          this.$listeners.fetch();
        }
      }
    },
    model_types: {
      get: function() {
        return this.value.types ? this.value.types : [];
      },
      set: function(newValue) {
        if (newValue) {
          this.$emit('input', {
            ...this.value,
            types: newValue ? newValue : []
          });

          this.$listeners.fetch();
        }
      }
    },
    model_insurers: {
      get: function() {
        return this.value.insurers ? this.value.insurers : [];
      },
      set: function(newValue) {
        if (newValue) {
          this.$emit('input', {
            ...this.value,
            insurers: newValue ? newValue : []
          });

          this.$listeners.fetch();
        }
      }
    },
    model_insureds: {
      get: function() {
        return this.value.insureds ? this.value.insureds : [];
      },
      set: function(newValue) {
        if (newValue) {
          this.$emit('input', {
            ...this.value,
            insureds: newValue ? newValue : []
          });

          this.$listeners.fetch();
        }
      }
    },
    ...mapState("lead_statuses", ["lead_statuses"]),
    ...mapState("lead_types", ["lead_types"]),
    ...mapState("service_managers", ["service_managers"])
  },
  watch: {
    search_insured: _.debounce(async function (value) {
      this.fetchInsured(value);
    }, 500),
    search_insurer: _.debounce(async function(value) {
      this.fetchInsurer(value);
    }, 500)
  },
  created() {
    this.loadStatuses();
    this.loadTypes();
    this.loadServiceManagers();
    this.fetchInsured();
    this.fetchInsurer();
  }
};
</script>

<style></style>
